import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const DealSummary = ({ deal, currentLanguage }) => {
  const translations = {
    en: {
      dealSummary: 'Deal Summary',
      dealId: 'Deal ID',
      itemDetails: 'Item Details',
      agreedFee: 'Agreed Carrier Fee',
      deliveryTimeline: 'Delivery Timeline',
      departure: 'Departure',
      arrival: 'Arrival',
      buyer: 'Buyer',
      traveler: 'Traveler',
      verified: 'Verified',
      rating: 'Rating',
      viewProfile: 'View Profile'
    },
    ko: {
      dealSummary: '거래 요약',
      dealId: '거래 ID',
      itemDetails: '상품 세부정보',
      agreedFee: '합의된 운송비',
      deliveryTimeline: '배송 일정',
      departure: '출발',
      arrival: '도착',
      buyer: '구매자',
      traveler: '여행자',
      verified: '인증됨',
      rating: '평점',
      viewProfile: '프로필 보기'
    }
  };

  const t = translations?.[currentLanguage];

  const formatCurrency = (amount) => {
    return currentLanguage === 'ko' 
      ? `₩${amount?.toLocaleString('ko-KR')}`
      : `$${amount?.toLocaleString('en-US')}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString(currentLanguage === 'ko' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const ParticipantCard = ({ participant, role }) => (
    <div className="bg-muted rounded-lg p-4 md:p-5">
      <div className="flex items-center gap-3 mb-3">
        <Image
          src={participant?.avatar}
          alt={participant?.avatarAlt}
          className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm md:text-base font-medium text-foreground truncate">
              {participant?.name}
            </h4>
            {participant?.verified && (
              <Icon name="BadgeCheck" size={16} color="var(--color-success)" />
            )}
          </div>
          <p className="text-xs md:text-sm text-muted-foreground caption">{role}</p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-border">
        <div className="flex items-center gap-2">
          <Icon name="Star" size={16} color="var(--color-warning)" />
          <span className="text-sm md:text-base font-medium data-text">
            {participant?.rating?.toFixed(1)}
          </span>
          <span className="text-xs md:text-sm text-muted-foreground caption">
            ({participant?.reviewCount})
          </span>
        </div>
        <button className="text-xs md:text-sm text-primary hover:underline caption">
          {t?.viewProfile}
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6 lg:p-8">
      <h3 className="text-lg md:text-xl lg:text-2xl font-heading font-semibold text-foreground mb-6 md:mb-8">
        {t?.dealSummary}
      </h3>
      <div className="space-y-6 md:space-y-8">
        <div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm md:text-base text-muted-foreground">{t?.dealId}</span>
            <span className="text-sm md:text-base font-medium text-foreground data-text">
              {deal?.id}
            </span>
          </div>
        </div>

        <div>
          <h4 className="text-sm md:text-base font-medium text-foreground mb-3">
            {t?.itemDetails}
          </h4>
          <div className="bg-muted rounded-lg p-4 md:p-5">
            <div className="flex items-start gap-4">
              <Image
                src={deal?.item?.image}
                alt={deal?.item?.imageAlt}
                className="w-16 h-16 md:w-20 md:h-20 rounded-lg object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h5 className="text-sm md:text-base font-medium text-foreground mb-2 line-clamp-2">
                  {deal?.item?.name}
                </h5>
                <div className="flex flex-wrap gap-2 mb-2">
                  <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md caption">
                    {deal?.item?.category}
                  </span>
                  <span className="px-2 py-1 bg-secondary/10 text-secondary text-xs rounded-md caption">
                    {deal?.item?.origin} → {deal?.item?.destination}
                  </span>
                </div>
                <p className="text-sm md:text-base font-semibold text-foreground data-text">
                  {formatCurrency(deal?.item?.budget)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between p-4 md:p-5 bg-accent/10 rounded-lg">
            <span className="text-sm md:text-base font-medium text-foreground">
              {t?.agreedFee}
            </span>
            <span className="text-lg md:text-xl font-bold text-accent data-text">
              {formatCurrency(deal?.carrierFee)}
            </span>
          </div>
        </div>

        <div>
          <h4 className="text-sm md:text-base font-medium text-foreground mb-3">
            {t?.deliveryTimeline}
          </h4>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 md:p-4 bg-muted rounded-lg">
              <Icon name="PlaneTakeoff" size={20} color="var(--color-primary)" />
              <div className="flex-1">
                <p className="text-xs md:text-sm text-muted-foreground caption">{t?.departure}</p>
                <p className="text-sm md:text-base font-medium text-foreground data-text">
                  {formatDate(deal?.timeline?.departure)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 md:p-4 bg-muted rounded-lg">
              <Icon name="PlaneLanding" size={20} color="var(--color-success)" />
              <div className="flex-1">
                <p className="text-xs md:text-sm text-muted-foreground caption">{t?.arrival}</p>
                <p className="text-sm md:text-base font-medium text-foreground data-text">
                  {formatDate(deal?.timeline?.arrival)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <ParticipantCard participant={deal?.buyer} role={t?.buyer} />
          <ParticipantCard participant={deal?.traveler} role={t?.traveler} />
        </div>
      </div>
    </div>
  );
};

export default DealSummary;